<template>
	<m-pageframe :title="mixin_app?.app_label || 'Loading...'">
		<v-container v-if="mixin_app">
			<v-row class="mb-10">
				<v-col
					cols="12"
					class="mb-0"
				>
					<v-row
						align="center"
						justify="space-between"
					>
						<v-col cols="auto">
							<link-back
								:fallback_route="{ name: 'app-search', params: { organization_id: $rc.getOrganizationId() } }"
							/>
						</v-col>
					</v-row>
				</v-col>

				<v-col
					cols="12"
					md="7"
				>
					<div class="d-block d-md-none mb-6">
						<app-details-card
							v-if="mixin_app"
							:app="mixin_app"
							:reloader="() => mixinGetAppById(mixin_app.app_id)"
						/>
					</div>

					<div class="mb-6">
						<v-carousel
							v-if="mixin_app.comp_feature_image_urls?.length"
							hide-delimiter-background
							show-arrows-on-hover
							height="400"
							class="image-carousel mb-4"
						>
							<v-carousel-item
								v-for="(src, i) in mixin_app.comp_feature_image_urls"
								:key="i"
							>
								<v-img
									:src="src"
									height="400"
									:contain="$vuetify.breakpoint.smAndDown"
									:alt="`${app_label} feature image ${i + 1}`"
								/>
							</v-carousel-item>
						</v-carousel>

						<div
							v-else
							class="fallback-image d-flex align-center justify-center mb-4"
						>
							<v-icon
								size="128"
								color="grey"
							>mdi-image-off</v-icon>
						</div>
					</div>

					<v-tabs
						v-model="active_tab_index"
						background-color="transparent"
						color="primary"
						grow
						class="mb-4"
					>
						<v-tab>Overview</v-tab>
						<v-tab>Pricing & Billing</v-tab>
						<v-tab>Permissions</v-tab>
						<v-tab>
							Versions
							<v-icon
								v-if="app_is_update_eligible"
								size="16"
								color="primary"
								class="ml-1"
							>mdi-alert-circle</v-icon>
						</v-tab>
					</v-tabs>

					<v-tabs-items v-model="active_tab_index">
						<v-tab-item>
							<div class="tab-content">
								<h2 class="text-h5 font-weight-bold mb-4">About This App</h2>
								<m-showdown
									:value="mixin_app.app_description_long_md || mixin_app.app_description_short_md"
								/>
							</div>
						</v-tab-item>

						<v-tab-item>
							<div class="tab-content">
								<template v-if="app_is_in_free_trial">
									<h2 class="text-h5 font-weight-bold mb-5">Pricing & Billing</h2>

									<v-card
										outlined
										class="mb-6"
									>
										<v-card-text>
											<h3 class="text-h6 mb-3">Free Trial Status</h3>

											<div class="mb-3">
												<v-row>
													<v-col cols="6">
														<div class="text-body-2 grey--text">Trial Ends</div>
														<div class="text-h6">{{ app_trial_end_date_pretty }}</div>
													</v-col>
													<v-col cols="6">
														<div class="text-body-2 grey--text">
															Monthly Fee After Trial
														</div>
														<div class="text-h6 primary--text">
															{{ app_monthly_fee_pretty }}
														</div>
													</v-col>
												</v-row>
											</div>

											<v-alert
												border="left"
												colored-border
												color="primary"
												dense
												class="shaded-light"
											>
												<div class="text-body-2">
													<strong>Important:</strong> Billing will automatically begin after
													your trial ends. You can cancel any time during the trial period to
													avoid charges.
												</div>
											</v-alert>
										</v-card-text>
									</v-card>
								</template>

								<h3 class="text-h5 font-weight-bold mb-4">Pricing & Billing FAQ</h3>
								<v-expansion-panels
									flat
									multiple
									class="faq-panels"
								>
									<v-expansion-panel v-if="app_has_free_trial">
										<v-expansion-panel-header>
											<span class="font-weight-medium">How do I cancel my free trial?</span>
										</v-expansion-panel-header>
										<v-expansion-panel-content>
											<p class="mb-2">
												You can cancel your free trial at any time during the trial period by
												clicking the &quot;Cancel Free Trial&quot; button on this page. Your
												access will immediately end, and you will not be charged.
											</p>
										</v-expansion-panel-content>
									</v-expansion-panel>

									<v-expansion-panel v-if="app_price_is_set && !app_is_free">
										<v-expansion-panel-header>
											<span class="font-weight-medium">When will I be charged?</span>
										</v-expansion-panel-header>
										<v-expansion-panel-content>
											<p class="mb-0">
												You will only be charged after your free trial period ends. Charges for
												this app will be applied to the standard RedChirp billing cycle for your
												organization.
											</p>
										</v-expansion-panel-content>
									</v-expansion-panel>

									<v-expansion-panel v-if="app_has_free_trial">
										<v-expansion-panel-header>
											<span class="font-weight-medium">Can I extend my free trial?</span>
										</v-expansion-panel-header>
										<v-expansion-panel-content>
											<p class="mb-0">
												Free trial extensions are handled on a case-by-case basis. Please
												contact our support team if you need additional time to evaluate the app
												before making a purchasing decision.
											</p>
										</v-expansion-panel-content>
									</v-expansion-panel>

									<v-expansion-panel>
										<v-expansion-panel-header>
											<span class="font-weight-medium">Is there a setup fee?</span>
										</v-expansion-panel-header>
										<v-expansion-panel-content>
											<p class="mb-0">
												No, there are no setup fees or hidden charges. <span
													v-if="app_price_is_set && !app_is_free"
												>You only
													pay the monthly
													subscription fee if applicable. All features and support are
													included in the base
													price.</span> <span v-else-if="app_price_is_set">All features are
													available for free.</span>
											</p>
										</v-expansion-panel-content>
									</v-expansion-panel>

									<v-expansion-panel>
										<v-expansion-panel-header>
											<span class="font-weight-medium">How do I cancel my subscription?</span>
										</v-expansion-panel-header>
										<v-expansion-panel-content>
											<p class="mb-0">
												You can cancel your subscription at any time from this page. <span
													v-if="app_price_is_set && !app_is_free"
												>Your access
													will continue until the end of your current billing period, and no
													further charges will be applied.</span><span v-else>Your access will
													immediately end.</span>
											</p>
										</v-expansion-panel-content>
									</v-expansion-panel>
								</v-expansion-panels>
							</div>
						</v-tab-item>

						<v-tab-item>
							<div class="tab-content">
								<h2 class="text-h5 font-weight-bold mb-4">Permissions Required</h2>

								<template v-if="mixin_app.app_permissions && mixin_app.app_permissions.length > 0">
									<p class="mb-4">
										By installing this app, you grant it the following permissions:
									</p>
									<app-permissions-list
										:permissions="mixin_app.app_permissions"
										list_class="pa-0"
									/>
								</template>

								<template v-else>
									<v-card
										outlined
										class="text-center pa-8"
									>
										<v-icon
											size="64"
											color="grey lighten-1"
											class="mb-4"
										>mdi-shield-check</v-icon>
										<h3 class="text-h6 mb-2">No Special Permissions Required</h3>
										<p class="text-body-2 grey--text mb-0">
											This app operates within standard security boundaries and does not require
											any special permissions to access your organization's data or features.
										</p>
									</v-card>
								</template>
							</div>
						</v-tab-item>

						<v-tab-item>
							<div class="tab-content">
								<h2 class="sr-only">Version Information</h2>

								<template v-if="app_is_update_eligible || app_is_installed || mixin_app.changelog_md">
									<template v-if="app_is_update_eligible">
										<v-alert
											border="left"
											colored-border
											color="primary"
											class="shaded mb-5"
											dense
										>
											<div class="d-flex align-center">
												<v-icon
													color="primary"
													class="ml-1 mr-3"
													aria-hidden="true"
												>mdi-alert-circle</v-icon>
												<div>
													<div class="font-weight-bold">A new version is available</div>
													<div class="text-body-2">
														Version {{ mixin_app.version }} is available for installation.
														You
														currently have version {{ mixin_app.installed_version }}
														installed.
														Please update to access the latest features and improvements.
													</div>
												</div>
											</div>
										</v-alert>
									</template>

									<div
										v-else-if="app_is_installed"
										class="mb-4"
									>
										<v-alert
											border="left"
											colored-border
											color="success"
											class="shaded mb-5"
											dense
										>
											<div class="d-flex align-center">
												<v-icon
													color="success"
													class="ml-1 mr-3"
												>mdi-check-circle</v-icon>
												<div>
													<div class="font-weight-bold">Your app is up to date</div>
													<div class="text-body-2">
														You have the latest version (v{{ mixin_app.version }}) installed
													</div>
												</div>
											</div>
										</v-alert>
									</div>

									<template v-if="mixin_app.changelog_md">
										<div class="mb-4">
											<h3 class="text-h5 font-weight-bold mb-0">
												Version {{ mixin_app.version }} Release Notes
											</h3>
										</div>

										<m-showdown
											:value="mixin_app.changelog_md"
											class="changelog-content mb-6"
										/>
									</template>
								</template>

								<template v-else>
									<p class="text-center text-body-2 grey--text">
										This app does not have any version information available.
									</p>
								</template>
							</div>
						</v-tab-item>
					</v-tabs-items>
				</v-col>

				<v-col
					cols="12"
					md="5"
					class="d-none d-md-block"
				>
					<app-details-card
						v-if="mixin_app"
						:app="mixin_app"
						:reloader="() => mixinGetAppById(mixin_app.app_id)"
					/>
				</v-col>
			</v-row>
		</v-container>

		<div
			v-else
			class="d-flex justify-center align-center"
			style="height: 400px;"
		>
			<v-progress-circular
				indeterminate
				color="primary"
				size="64"
			/>
		</div>
	</m-pageframe>
</template>

<script>
import AppInstallation from '@/mixins/app-installation.js';
import AppDerived from '@/mixins/app-derived.js';
import UrlTabSync from '../../mixins/url-tab-sync.js'; // relative import for JSDoc
import AppPermissionsList from '@/components/app-store/app-permissions-list.vue';
import AppDetailsCard from '@/components/app-store/app-details-card.vue';

export default {
	mixins: [AppInstallation, AppDerived, UrlTabSync],

	components: {
		AppPermissionsList,
		AppDetailsCard,
	},

	data() {
		return {
			mixin_tab_config: {
				tab_mapping: {
					overview: 0,
					pricing: 1,
					permissions: 2,
					versions: 3
				}
			}
		};
	},

	mounted() {
		this.mixinGetAppById(this.$route.params.app_id);
	},

	computed: {
		changelog_summary() {
			if (!this.mixin_app?.changelog_md) return '';
			const lines = this.mixin_app?.changelog_md.split('\n');
			const first_line = lines.find(line => line.trim().length > 0);
			return first_line ? first_line.replace(/^#+\s*/, '').trim() : 'Version update';
		},
	}
};
</script>

<style lang="scss" scoped>
.container {
	max-width: 80rem;
}

.image-carousel {
	background-color: var(--color-bg-shaded-light);
	border-radius: var(--border-radius-default);
	overflow: hidden;

	:deep(img) {
		border-radius: var(--border-radius-default);
	}
}

.fallback-image {
	height: 400px;
	background-color: var(--color-bg-shaded-light);
	border-radius: var(--border-radius-default);
}

.tab-content {
	padding: 1.5rem 0;
	min-height: 300px;
}

:deep(.v-tabs) {
	border-bottom: 1px solid #e0e0e0;
}

:deep(.v-tab) {
	text-transform: none;
	font-weight: 500;
	letter-spacing: normal;
}

.faq-panels {
	margin-bottom: 5rem;

	:deep(.v-expansion-panel) {
		border-radius: var(--border-radius-default) !important;
		margin-bottom: 0.5rem;

		&:last-child {
			margin-bottom: 0;
		}
	}

	:deep(.v-expansion-panel-header) {
		padding: 1rem 1.5rem;
		min-height: auto;
		background-color: var(--color-bg-shaded-light);
	}

	:deep(.v-expansion-panel-content__wrap) {
		padding: 1rem;
	}
}

.version-details {
	:deep(.v-simple-table) {
		border-radius: var(--border-radius-default);
	}

	:deep(table) {
		border-collapse: separate;
		border-spacing: 0;
	}

	:deep(tbody tr:not(:last-child) td) {
		border-bottom: 1px solid #e0e0e0;
	}

	.version-label {
		font-weight: 500;
		color: rgba(0, 0, 0, 0.6);
		padding: 1rem 1.5rem;
		width: 150px;
		vertical-align: top;
		background-color: var(--color-bg-shaded-light);
	}

	.version-value {
		padding: 1rem 1.5rem;
		vertical-align: top;
	}
}
</style>